import { ReactNode, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Chart, ChartArea, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries,
  ChartSeriesItem, ChartTooltip, ChartValueAxis, ChartValueAxisItem, Sankey,
} from '@progress/kendo-react-charts';
import type { TooltipContentProps, TooltipContext, SankeyNodeDataItem, SankeyLinkDataItem } from '@progress/kendo-react-charts';
import {
  QuoteOfferCallRow, QuoteOfferCallsSummary, RESPONSE, attentionIssue, byDay, byHour, byVehicleType,
  declineReasons, inLocalRange, isAccepted, pct, stageOf, summarize, unitOf,
} from './quoteOfferCallStats';

export type { QuoteOfferCallsSummary };

const C = {
  accept: '#22c55e',
  call: '#3b82f6',
  decline: '#f97316',
  idle: '#64748b',
  bad: '#ef4444',
  axis: '#94a3b8',
  grid: 'rgba(148,163,184,0.18)',
};

const STAGE_LABELS: Record<string, string> = {
  reached: 'Reached', voicemail: 'Voicemail', noAnswer: 'No Answer', busy: 'Busy', failed: 'Failed', pending: 'No Result Yet',
};

const RESPONSE_LABELS: Record<number, string> = {
  [RESPONSE.None]: '—',
  [RESPONSE.Accepted]: 'Accepted',
  [RESPONSE.Declined]: 'Declined',
  [RESPONSE.AcceptedWithNewPickup]: 'Accepted, New Times',
  [RESPONSE.LeftForApp]: 'Left for App',
  [RESPONSE.WrongNumber]: 'Wrong Number',
  [RESPONSE.Unclear]: 'Unclear',
  [RESPONSE.AnsweredInApp]: 'Answered in App',
  [RESPONSE.OfferClosed]: 'Offer Closed',
};

const SankeyNodeTooltip: React.FC<TooltipContentProps> = ({ dataItem, nodeValue }) => (
  <span>{(dataItem as SankeyNodeDataItem).label?.text}: {nodeValue}</span>
);

const SankeyLinkTooltip: React.FC<TooltipContentProps> = ({ dataItem }) => {
  const link = dataItem as SankeyLinkDataItem;
  return <span>{link.source.label?.text} &rarr; {link.target.label?.text}: {link.value}</span>;
};

function percent(value: number | null): string {
  return value == null ? '—' : `${value}%`;
}

function formatSeconds(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function usd(value: number | null | undefined, digits = 2): string {
  if (value == null) return '—';
  return `$${value.toFixed(digits)}`;
}

function localTime(utc: string): string {
  return dayjs(utc).format('MMM D, h:mm A');
}

function hourLabel(hour: number): string {
  return dayjs().hour(hour).format('ha');
}

function newTimes(r: QuoteOfferCallRow): string | null {
  const parts = [
    r.AlternatePickupUtc ? `new pickup ${localTime(r.AlternatePickupUtc)}` : null,
    r.AlternateDeliveryUtc ? `new delivery ${localTime(r.AlternateDeliveryUtc)}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function unitLabel(r: QuoteOfferCallRow): string {
  return [r.VehicleNumber, r.DriverName || r.DriverNumber].filter(Boolean).join(' · ') || '—';
}

function Panel({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <div className="card h-100 shadow-sm">
      <div className="card-body p-3 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-baseline mb-2">
          <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>{title}</h6>
          {note && <small className="text-muted" style={{ fontSize: '0.7rem' }}>{note}</small>}
        </div>
        <div className="flex-grow-1">{children}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: ReactNode; color?: string }) {
  return (
    <div className="card h-100 shadow-sm">
      <div className="card-body p-3">
        <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.06em' }}>{label}</div>
        <div className="d-flex align-items-baseline mt-1">
          <span className="font-weight-bold" style={{ fontSize: '1.75rem', color: color ?? 'inherit' }}>{value}</span>
          {sub && <span className="text-muted ml-2" style={{ fontSize: '0.75rem' }}>{sub}</span>}
        </div>
      </div>
    </div>
  );
}

// ---- Collapsed card ----
export function QuoteOfferCallsCard({ summary, from, to, selected, onClick }: {
  summary: QuoteOfferCallsSummary; from: Date; to: Date; selected: boolean; onClick: () => void;
}) {
  const s = useMemo(() => summarize(inLocalRange(summary.Calls ?? [], from, to)), [summary, from, to]);

  return (
    <div
      className={`card h-100 shadow-sm${selected ? ' border-primary' : ''}`}
      style={{ cursor: 'pointer', ...(selected ? { borderLeft: `4px solid ${C.call}` } : {}) }}
      onClick={onClick}
    >
      <div className="card-body">
        <h6 className="card-title font-weight-bold">Quote Offer Calls</h6>
        <div className="mb-2">
          <span className="font-weight-bold" style={{ color: C.call, fontSize: '2rem' }}>{s.placed.toLocaleString()}</span>
          <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>calls placed</span>
        </div>
        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
          <strong>{percent(s.reachRate)}</strong> reached a driver &nbsp;
          <strong>{percent(s.acceptRate)}</strong> of those accepted
          {s.costUsd > 0 && <> &nbsp; <strong>{usd(s.costUsd)}</strong> spend</>}
        </div>
      </div>
    </div>
  );
}

// ---- Paging for the long panels ----
const ROWS = 10;

function usePage<T>(items: T[], size = ROWS) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(items.length / size));
  const safePage = Math.min(page, pageCount - 1);
  return { rows: items.slice(safePage * size, safePage * size + size), page: safePage, pageCount, setPage, total: items.length };
}

function Pager({ page, pageCount, setPage, total, noun }: {
  page: number; pageCount: number; setPage: (p: number) => void; total: number; noun: string;
}) {
  if (pageCount <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-1">
      <small className="text-muted">{total} {noun} &middot; page {page + 1} of {pageCount}</small>
      <div className="btn-group btn-group-sm">
        <button type="button" className="btn btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
        <button type="button" className="btn btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  );
}

// ---- By vehicle type, then unit, then that unit's calls ----
type Totals = { calls: number; reached: number; accepted: number; declined: number; notReached: number; acceptRate: number | null };
type UnitRow = ReturnType<typeof byVehicleType>[number]['units'][number];

const COLUMNS = ['34%', '11%', '11%', '11%', '11%', '11%', '11%'];

function Columns() {
  return <colgroup>{COLUMNS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>;
}

function TotalCells({ t }: { t: Totals }) {
  return (
    <>
      <td className="text-right">{t.calls}</td>
      <td className="text-right">{t.reached}</td>
      <td className="text-right">{t.accepted}</td>
      <td className="text-right">{t.declined}</td>
      <td className="text-right">{t.notReached}</td>
      <td className="text-right">{percent(t.acceptRate)}</td>
    </>
  );
}

function Toggle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary mr-2"
      style={{ width: 22, height: 22, padding: 0, lineHeight: '18px', fontSize: '0.8rem' }}
      aria-label={label}
      aria-expanded={open}
      onClick={e => { e.stopPropagation(); onClick(); }}
    >
      {open ? '\u2212' : '+'}
    </button>
  );
}

function outcomeLabel(r: QuoteOfferCallRow): string {
  return r.Response !== RESPONSE.None ? RESPONSE_LABELS[r.Response] ?? String(r.Response) : STAGE_LABELS[stageOf(r)];
}

function UnitCalls({ calls }: { calls: QuoteOfferCallRow[] }) {
  const { rows, ...pager } = usePage(calls);
  return (
    <div className="pl-5 pr-2 pb-2">
      <table className="table table-sm mb-0" style={{ fontSize: '0.75rem' }}>
        <tbody>
          {rows.map(r => (
            <tr key={r.CallID}>
              <td className="text-nowrap text-muted">{localTime(r.CreatedUtc)}</td>
              <td className="text-nowrap">
                <a href={`/Quote/Index/${r.QuoteID}`} target="_blank" rel="noopener noreferrer">#{r.QuoteID}</a>
              </td>
              <td className="text-nowrap">{outcomeLabel(r)}</td>
              <td>{r.DeclineReason || r.ResponseDetail || newTimes(r) || ''}</td>
              <td className="text-nowrap text-right text-muted">{r.DurationMs != null ? formatSeconds(r.DurationMs / 1000) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pager {...pager} noun="calls" />
    </div>
  );
}

function UnitList({ units }: { units: UnitRow[] }) {
  const { rows, ...pager } = usePage(units);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="pl-4">
      <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.8rem', tableLayout: 'fixed' }}>
        <Columns />
        <tbody>
          {rows.map(u => (
            <UnitLine key={u.unit} unit={u} open={open === u.unit} onToggle={() => setOpen(open === u.unit ? null : u.unit)} />
          ))}
        </tbody>
      </table>
      <Pager {...pager} noun="vehicles" />
    </div>
  );
}

function UnitLine({ unit, open, onToggle }: { unit: UnitRow; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr>
        <td>
          <div className="d-flex align-items-start">
            <Toggle open={open} onClick={onToggle} label={`Show calls for unit ${unit.unit}`} />
            <div style={{ minWidth: 0 }}>
              <strong>{unit.unit}</strong>
              {unit.drivers && <span className="text-muted"> &middot; {unit.drivers}</span>}
              <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                Latest: {localTime(unit.latest.CreatedUtc)} &middot; {outcomeLabel(unit.latest)}
              </div>
            </div>
          </div>
        </td>
        <TotalCells t={unit} />
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="p-0 border-top-0">
            <UnitCalls calls={unit.newestFirst} />
          </td>
        </tr>
      )}
    </>
  );
}

function VehicleBreakdown({ rows }: { rows: QuoteOfferCallRow[] }) {
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const types = useMemo(
    () => byVehicleType(query ? rows.filter(r => `${unitOf(r)} ${r.DriverName ?? ''}`.toLowerCase().includes(query)) : rows),
    [rows, query]);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="table-responsive">
      <div className="d-flex justify-content-end mb-2">
        <input
          type="search"
          className="form-control form-control-sm"
          style={{ maxWidth: 200, fontSize: '0.75rem' }}
          placeholder="Search Truck No. or Driver"
          aria-label="Search Truck No. or Driver"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      {query && types.length === 0 && <div className="text-muted small mb-2">No trucks or drivers match "{search.trim()}".</div>}
      <table className="table table-sm mb-0" style={{ fontSize: '0.8rem', tableLayout: 'fixed' }}>
        <Columns />
        <thead>
          <tr>
            <th>Vehicle Type</th>
            <th className="text-right">Calls</th>
            <th className="text-right">Reached</th>
            <th className="text-right">Accepted</th>
            <th className="text-right">Declined</th>
            <th className="text-right">Not Reached</th>
            <th className="text-right">Accept Rate</th>
          </tr>
        </thead>
        <tbody>
          {types.map(t => (
            <VehicleTypeLine key={t.type} type={t} open={query !== '' || open === t.type} onToggle={() => setOpen(open === t.type ? null : t.type)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VehicleTypeLine({ type, open, onToggle }: { type: ReturnType<typeof byVehicleType>[number]; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={onToggle}>
        <td>
          <div className="d-flex align-items-center">
            <Toggle open={open} onClick={onToggle} label={`Show ${type.type} vehicles`} />
            <strong>{type.type}</strong>
            <span className="text-muted ml-1">&middot; {type.unitCount} vehicle{type.unitCount === 1 ? '' : 's'}</span>
          </div>
        </td>
        <TotalCells t={type} />
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="p-0 border-top-0" onClick={e => e.stopPropagation()}>
            <UnitList units={type.units} />
          </td>
        </tr>
      )}
    </>
  );
}

function AttentionList({ items }: { items: { row: QuoteOfferCallRow; issue: { label: string; detail: string } | null }[] }) {
  const { rows, ...pager } = usePage(items);
  return (
    <>
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.8rem' }}>
          <tbody>
            {rows.map(({ row, issue }) => (
              <tr key={row.CallID}>
                <td className="text-nowrap" style={{ color: C.bad }}>{issue?.label}</td>
                <td className="text-nowrap">{localTime(row.CreatedUtc)}</td>
                <td className="text-nowrap">
                  <a href={`/Quote/Index/${row.QuoteID}`} target="_blank" rel="noopener noreferrer">#{row.QuoteID}</a>
                </td>
                <td className="text-nowrap">{unitLabel(row)}</td>
                <td>{issue?.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager {...pager} noun="calls" />
    </>
  );
}

// ---- Full call list ----
const PAGE_SIZE = 15;

const CALL_FILTERS: { key: string; label: string; match: (r: QuoteOfferCallRow) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'accepted', label: 'Accepted', match: isAccepted },
  { key: 'declined', label: 'Declined', match: r => r.Response === RESPONSE.Declined },
  { key: 'inapp', label: 'Answered in App', match: r => r.Response === RESPONSE.AnsweredInApp },
  { key: 'nocontact', label: 'No Contact', match: r => stageOf(r) !== 'reached' },
  { key: 'hangup', label: 'Driver Hung Up', match: r => r.DisconnectionReason === 'user_hangup' },
];

const localDay = (r: QuoteOfferCallRow) => dayjs(r.CreatedUtc).format('YYYY-MM-DD');

function CallTable({ calls }: { calls: QuoteOfferCallRow[] }) {
  const days = useMemo(() => Array.from(new Set(calls.map(localDay))).sort().reverse(), [calls]);

  const [day, setDay] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);

  const activeDay = days.includes(day) ? day : '';
  const activeFilter = CALL_FILTERS.find(f => f.key === filter) ?? CALL_FILTERS[0];

  const rows = useMemo(
    () => calls.filter(r => (activeDay === '' || localDay(r) === activeDay) && activeFilter.match(r)),
    [calls, activeDay, activeFilter]);

  const reset = (fn: () => void) => { fn(); setPage(0); };

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  return (
    <>
      <div className="d-flex justify-content-end align-items-center flex-wrap mb-2">
        <div className="btn-group btn-group-sm mr-2">
          {CALL_FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{ fontSize: '0.7rem' }}
              onClick={() => reset(() => setFilter(f.key))}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select
          className="form-control form-control-sm"
          style={{ width: 'auto' }}
          value={activeDay}
          onChange={e => reset(() => setDay(e.target.value))}
        >
          <option value="">All days</option>
          {days.map(d => <option key={d} value={d}>{dayjs(d).format('ddd, MMM D, YYYY')}</option>)}
        </select>
      </div>
      {rows.length === 0 && <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>No calls match this filter.</p>}
      <div className="table-responsive">
        <table className="table table-sm table-hover mb-2" style={{ fontSize: '0.8rem' }}>
          <thead>
            <tr>
              <th>Time</th>
              <th>Quote</th>
              <th>Unit / Driver</th>
              <th>Reach</th>
              <th>Response</th>
              <th>Detail</th>
              <th>Duration</th>
              <th>Cost</th>
              <th className="text-right">Links</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(r => (
              <tr key={r.CallID}>
                <td className="text-nowrap">{localTime(r.CreatedUtc)}</td>
                <td className="text-nowrap">
                  <a href={`/Quote/Index/${r.QuoteID}`} target="_blank" rel="noopener noreferrer">#{r.QuoteID}</a>
                </td>
                <td className="text-nowrap">{unitLabel(r)}</td>
                <td className="text-nowrap">{STAGE_LABELS[stageOf(r)]}</td>
                <td className="text-nowrap">
                  {RESPONSE_LABELS[r.Response] ?? r.Response}
                  {r.RateRequested && <span className="badge badge-light border ml-1">asked about pay</span>}
                </td>
                <td>{r.DeclineReason || r.ResponseDetail || newTimes(r) || '—'}</td>
                <td className="text-nowrap">{r.DurationMs != null ? formatSeconds(r.DurationMs / 1000) : '—'}</td>
                <td className="text-nowrap">{r.CostCents != null ? usd(r.CostCents / 100, 3) : '—'}</td>
                <td className="text-right text-nowrap">
                  {r.RetellCallID ? (
                    <a
                      href={`https://dashboard.retellai.com/call-history?call_id=${r.RetellCallID}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Listen to the call in Retell"
                    >
                      Call
                    </a>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          {rows.length} call{rows.length === 1 ? '' : 's'} &middot; page {safePage + 1} of {pageCount}
        </small>
        <div className="btn-group btn-group-sm">
          <button type="button" className="btn btn-outline-secondary" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</button>
          <button type="button" className="btn btn-outline-secondary" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>Next</button>
        </div>
      </div>
    </>
  );
}

// ---- Detail panel ----
export function QuoteOfferCallsDetail({ summary, from, to, onClose }: {
  summary: QuoteOfferCallsSummary; from: Date; to: Date; onClose: () => void;
}) {
  const rows = useMemo(() => inLocalRange(summary.Calls ?? [], from, to), [summary, from, to]);
  const s = useMemo(() => summarize(rows), [rows]);
  const reasons = useMemo(() => declineReasons(rows), [rows]);
  const hours = useMemo(() => byHour(rows), [rows]);
  const days = useMemo(() => byDay(rows, from, to), [rows, from, to]);
  const attention = useMemo(() => {
    const now = new Date();
    return rows.map(r => ({ row: r, issue: attentionIssue(r, now) })).filter(x => x.issue != null);
  }, [rows]);
  const [showAll, setShowAll] = useState(false);

  const sankeyData = useMemo(() => {
    const nodes = [
      { id: 'Calls Placed', color: C.idle },
      { id: 'Reached Driver', color: C.call },
      { id: 'Voicemail', color: C.idle },
      { id: 'No Answer', color: C.idle },
      { id: 'Busy', color: C.idle },
      { id: 'Failed', color: C.bad },
      { id: 'No Result Yet', color: C.idle },
      { id: 'Accepted', color: C.accept },
      { id: 'Accepted, New Times', color: C.accept },
      { id: 'Declined', color: C.decline },
      { id: 'Left for App', color: C.idle },
      { id: 'Unclear or Wrong Number', color: C.bad },
      { id: 'Answered in App', color: C.accept },
      { id: 'Offer Closed', color: C.idle },
      { id: 'Hung Up, No Answer Given', color: C.idle },
    ];
    const links = [
      { sourceId: 'Calls Placed', targetId: 'Reached Driver', value: s.reached },
      { sourceId: 'Calls Placed', targetId: 'Voicemail', value: s.voicemail },
      { sourceId: 'Calls Placed', targetId: 'No Answer', value: s.noAnswer },
      { sourceId: 'Calls Placed', targetId: 'Busy', value: s.busy },
      { sourceId: 'Calls Placed', targetId: 'Failed', value: s.failed },
      { sourceId: 'Calls Placed', targetId: 'No Result Yet', value: s.pending },
      { sourceId: 'Reached Driver', targetId: 'Accepted', value: s.accepted },
      { sourceId: 'Reached Driver', targetId: 'Accepted, New Times', value: s.acceptedLater },
      { sourceId: 'Reached Driver', targetId: 'Declined', value: s.declined },
      { sourceId: 'Reached Driver', targetId: 'Left for App', value: s.leftForApp },
      { sourceId: 'Reached Driver', targetId: 'Unclear or Wrong Number', value: s.unclear },
      { sourceId: 'Reached Driver', targetId: 'Answered in App', value: s.answeredInApp },
      { sourceId: 'Reached Driver', targetId: 'Offer Closed', value: s.offerClosed },
      { sourceId: 'Reached Driver', targetId: 'Hung Up, No Answer Given', value: s.noAnswerGiven },
    ].filter(l => l.value > 0);

    const used = new Set<string>();
    links.forEach(l => { used.add(l.sourceId); used.add(l.targetId); });

    return {
      nodes: nodes.filter(n => used.has(n.id)).map(n => ({ ...n, label: { text: n.id } })),
      links,
    };
  }, [s]);

  const reasonRows = useMemo(() => [...reasons].slice(0, 6).reverse(), [reasons]);

  const trendSeries = [
    { name: 'Accepted', color: C.accept, data: days.map(d => d.accepted) },
    { name: 'Declined', color: C.decline, data: days.map(d => d.declined) },
    { name: 'Reached, Other', color: C.call, data: days.map(d => d.otherReached) },
    { name: 'Not Reached', color: C.idle, data: days.map(d => d.notReached) },
  ];

  return (
    <div className="card mt-3 shadow-sm">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <span className="rounded-circle mr-2" style={{ display: 'inline-block', width: 10, height: 10, background: C.call }} />
          <strong>Quote Offer Calls</strong>
          <span className="text-muted ml-2" style={{ fontSize: '0.85rem' }}>— selected date range, your local time</span>
        </div>
        <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
      </div>

      <div className="card-body">
        {s.placed === 0 ? (
          <p className="text-muted mb-0">No calls in this period.</p>
        ) : (
          <>
            {/* Headline numbers */}
            <div className="row">
              <div className="col-md mb-3">
                <StatCard label="Reach Rate" value={percent(s.reachRate)} sub={`${s.reached} of ${s.placed} calls`} />
              </div>
              <div className="col-md mb-3">
                <StatCard label="Answer Rate" value={percent(s.answerRate)} sub="of drivers reached" />
              </div>
              <div className="col-md mb-3">
                <StatCard label="Accept Rate" value={percent(s.acceptRate)} sub="of drivers reached" color={C.accept} />
              </div>
              <div className="col-md mb-3">
                <StatCard
                  label="Median Time to Answer"
                  value={formatSeconds(s.medianSecondsToAnswer)}
                  sub={s.medianDurationMs != null ? `${formatSeconds(s.medianDurationMs / 1000)} median call` : undefined}
                />
              </div>
              <div className="col-md mb-3">
                <StatCard
                  label="Asked for Different Pay"
                  value={s.rateRequested.toLocaleString()}
                  sub={s.reached > 0 ? `${percent(pct(s.rateRequested, s.reached))} of reached` : undefined}
                />
              </div>
              <div className="col-md mb-3">
                <StatCard
                  label="Avg Cost / Call"
                  value={usd(s.costPerCallUsd, 3)}
                  sub={s.costCalls > 0 ? <>{usd(s.costUsd)} total &middot; {usd(s.costPerAcceptedUsd, 3)} / accepted load</> : 'no cost recorded yet'}
                />
              </div>
            </div>

            {/* Flow */}
            <div className="row">
              <div className="col-lg-8 mb-3">
                <Panel title="Call Flow" note="every call, from dialing to outcome">
                  <Sankey
                    style={{ height: 360, width: '100%' }}
                    data={sankeyData}
                    nodes={{ width: 14, padding: 14 }}
                    legend={{ visible: false }}
                    links={{ colorType: 'target', opacity: 0.35 }}
                    labels={{ color: C.axis, font: '11px sans-serif' }}
                    tooltip={{ nodeComponent: SankeyNodeTooltip, linkComponent: SankeyLinkTooltip }}
                  />
                </Panel>
              </div>
              <div className="col-lg-4 mb-3">
                <div className="d-flex flex-column h-100">
                  <div className="flex-grow-1 mb-3">
                    <Panel title="Why Drivers Said No" note={`${s.declined} declined`}>
                      {reasonRows.length === 0 ? (
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No declines in this period.</p>
                      ) : (
                        <Chart style={{ height: Math.max(120, reasonRows.length * 30 + 30) }}>
                          <ChartArea background="transparent" />
                          <ChartLegend visible={false} />
                          <ChartTooltip render={({ point }: TooltipContext) => (
                            <span>{String(point.category)}: {point.value}</span>
                          )} />
                          <ChartCategoryAxis>
                            <ChartCategoryAxisItem
                              categories={reasonRows.map(r => r.reason)}
                              labels={{ color: C.axis, font: '11px sans-serif' }}
                              line={{ visible: false }}
                              majorTicks={{ visible: false }}
                              majorGridLines={{ visible: false }}
                            />
                          </ChartCategoryAxis>
                          <ChartValueAxis>
                            <ChartValueAxisItem visible={false} majorGridLines={{ visible: false }} />
                          </ChartValueAxis>
                          <ChartSeries>
                            <ChartSeriesItem
                              type="bar"
                              data={reasonRows.map(r => r.count)}
                              color={C.decline}
                              gap={0.6}
                              labels={{ visible: true, position: 'outsideEnd', color: C.axis, font: '10px sans-serif' }}
                            />
                          </ChartSeries>
                        </Chart>
                      )}
                    </Panel>
                  </div>
                  <div>
                    <Panel title="Pay Requests" note="pay can only change in the app">
                      <div className="d-flex align-items-baseline">
                        <span className="font-weight-bold" style={{ fontSize: '1.6rem' }}>{s.rateRequested}</span>
                        <span className="text-muted ml-2" style={{ fontSize: '0.75rem' }}>of {s.reached} drivers reached</span>
                      </div>
                      <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.78rem' }}>
                        <span className="text-muted">Went on to decline</span>
                        <strong>{s.rateRequestedDeclined}</strong>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem' }}>
                        <span className="text-muted">Accepted anyway</span>
                        <strong>{s.rateRequestedAccepted}</strong>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem' }}>
                        <span className="text-muted">No decision on the call</span>
                        <strong>{s.rateRequested - s.rateRequestedDeclined - s.rateRequestedAccepted}</strong>
                      </div>
                    </Panel>
                  </div>
                </div>
              </div>
            </div>

            {/* By vehicle type, then unit */}
            <div className="row">
              <div className="col-12 mb-3">
                <Panel title="By Vehicle" note="click + to see a type's vehicles, then a vehicle's calls, newest first; accept rate is of drivers reached">
                  <VehicleBreakdown rows={rows} />
                </Panel>
              </div>
            </div>

            {/* Daily trend */}
            <div className="row">
              <div className="col-lg-7 mb-3">
                <Panel title="Daily Trend" note="calls per day by outcome">
                  <Chart style={{ height: 240 }}>
                    <ChartArea background="transparent" />
                    <ChartTooltip shared={true} />
                    <ChartLegend position="bottom" labels={{ color: C.axis, font: '11px sans-serif' }} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem
                        categories={days.map(d => dayjs(d.day).format('MMM D'))}
                        labels={{ color: C.axis, font: '10px sans-serif', rotation: days.length > 10 ? 35 : 0 }}
                        line={{ color: C.grid }}
                        majorGridLines={{ visible: false }}
                      />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem
                        labels={{ color: C.axis, font: '10px sans-serif' }}
                        line={{ visible: false }}
                        majorGridLines={{ color: C.grid }}
                      />
                    </ChartValueAxis>
                    <ChartSeries>
                      {trendSeries.map(t => (
                        <ChartSeriesItem key={t.name} type="column" stack={{ group: 'calls' }} name={t.name} color={t.color} data={t.data} />
                      ))}
                    </ChartSeries>
                  </Chart>
                </Panel>
              </div>
              <div className="col-lg-5 mb-3">
                <Panel title="Reach Rate by Hour" note="your local time">
                  <Chart style={{ height: 240 }}>
                    <ChartArea background="transparent" />
                    <ChartLegend visible={false} />
                    <ChartTooltip render={({ point }: TooltipContext) => (
                      <span>{String(point.category)}: {point.dataItem.reached} of {point.dataItem.calls} reached ({point.value}%)</span>
                    )} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem
                        categories={hours.map(h => hourLabel(h.hour))}
                        labels={{ color: C.axis, font: '10px sans-serif', step: 3 }}
                        line={{ color: C.grid }}
                        majorGridLines={{ visible: false }}
                      />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem
                        min={0}
                        max={100}
                        labels={{ color: C.axis, font: '10px sans-serif', format: '{0}%' }}
                        line={{ visible: false }}
                        majorGridLines={{ color: C.grid }}
                      />
                    </ChartValueAxis>
                    <ChartSeries>
                      <ChartSeriesItem type="column" data={hours} field="rate" color={C.call} gap={0.4} />
                    </ChartSeries>
                  </Chart>
                </Panel>
              </div>
            </div>

            {/* Needs attention */}
            <div className="row">
              <div className="col-12 mb-3">
                <Panel title="Needs Attention" note={`${attention.length} call${attention.length === 1 ? '' : 's'}`}>
                  {attention.length === 0 ? (
                    <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Nothing to look at in this period.</p>
                  ) : (
                    <AttentionList items={attention} />
                  )}
                </Panel>
              </div>
            </div>

            {/* All calls */}
            <div className="card shadow-sm">
              <div className="card-body p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>All Calls</h6>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowAll(!showAll)}>
                    {showAll ? 'Hide' : `Show ${rows.length}`}
                  </button>
                </div>
                {showAll && <div className="mt-2"><CallTable calls={rows} /></div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
